/* The one table for the builder and the console.
 *
 * WHAT IT FIXES
 * -------------
 * The audit counted four different action grammars in the builder alone
 * (red text links, unlabelled grey icons, "Editar →", four inline text
 * buttons) and two different header treatments (sentence case in some
 * screens, 11 px uppercase in others, a solid violet band in the whole
 * console). Here there is one of each:
 *
 *   header  12 px, sentence case, semibold, neutral-600 on neutral-50
 *   row     48 px, hover neutral-50, hairline neutral-100
 *   actions <= 2 render inline with a label; >= 3 collapse to a kebab
 *
 * The destructive action is always last, is the only tinted one, and the
 * caller is expected to confirm it.
 *
 * NATIVE ``<table>`` IS ALLOWED HERE and nowhere else in the two admin
 * apps; the CI gate is scoped to exclude this package. A table is the
 * right element for tabular data and the kit's own Table does not expose
 * the row-action grammar we need.
 *
 * NO ROW CAPS. There is no ``slice`` in this file and there never will
 * be: "somos BI, no cortamos data". Long lists get windowing, which
 * lands with the screens that need it (the 769-row issues queue and the
 * 4 698-row send audit).
 */

import { DropdownMenu } from "@omnitok/ui";
import { MoreHorizontal } from "lucide-react";
import { Fragment, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "./layout";

export interface Column<T> {
  /** Stable id, also the React key for the cell. */
  id: string;
  /** Sentence case. Never uppercase — that is the point. */
  header: string;
  cell: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
  /** A Tailwind width utility, e.g. ``w-40``. */
  width?: string;
  /** Header-only hint, rendered as a native tooltip. */
  headerTitle?: string;
}

export interface RowAction<T> {
  id: string;
  /** Always present. An icon-only action is a puzzle, not an affordance. */
  label: string;
  /** A function when the glyph depends on the row — a spinner while that
   *  row's delete is in flight, say. */
  icon?: ReactNode | ((row: T) => ReactNode);
  onSelect: (row: T) => void;
  /** Destructive actions render last and tinted, and must confirm. */
  danger?: boolean;
  disabled?: (row: T) => boolean;
  /** Hide this action for some rows without changing the column shape. */
  hidden?: (row: T) => boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  actions?: RowAction<T>[];
  /** Header for the actions column. Usually left blank. */
  actionsHeader?: string;
  /** Rendered in place of the body when ``rows`` is empty. Pass an
   *  EmptyState; a sentence centred in a tbody is not an empty state. */
  empty?: ReactNode;
  /** Sits BELOW the table, never as a spanning row inside it. */
  footnote?: string;
  /** Accessible caption, visually hidden. */
  caption?: string;
  onRowClick?: (row: T) => void;
  /** Extra attributes for a row — drag handlers, aria, a highlight class.
   *  Exists so a sortable list (the report rows of a project) can still
   *  be THIS table instead of a hand-rolled second one. */
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement>;
  /** A narrow first cell before the columns, for a drag handle. The
   *  header cell is rendered empty and aria-hidden. */
  leadingCell?: (row: T) => ReactNode;
  /** A detail row rendered UNDER this row, spanning every column.
   *  Return null for rows that are not expanded. Exists so a table with
   *  a history drawer (the delivery subscriptions and their jobs) can be
   *  THIS table rather than a hand-rolled second one — the alternative
   *  was dropping the drawer to satisfy a style rule. */
  expandedRow?: (row: T) => ReactNode | null;
  className?: string;
}

const ALIGN: Record<NonNullable<Column<never>["align"]>, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  actions,
  actionsHeader,
  empty,
  footnote,
  caption,
  onRowClick,
  rowProps,
  leadingCell,
  expandedRow,
  className,
}: DataTableProps<T>): ReactNode {
  const hasActions = actions !== undefined && actions.length > 0;
  const isEmpty = rows.length === 0 && empty !== undefined;

  // The footnote renders in BOTH states. It used to sit after an early
  // return, so a caller that passed `empty` AND `footnote` lost the note
  // exactly when the list was empty — which is when an explanatory note
  // is most likely to be the only thing on screen. Caught in review
  // before it shipped: the project Access pane pairs the two, and its
  // note is the sentence that explains why admins are not listed.
  // `relative` on the scroll wrapper below is load-bearing, not cosmetic.
  // The caption is `sr-only`, which positions it ABSOLUTELY; without a
  // positioned ancestor inside the app's scroll area its containing block
  // is the document, so a caption that lands at 1,744px stretches the
  // document to 1,745px and the WINDOW grows a second scrollbar next to
  // <main>'s. Seen on the scope detail page in staging the day Plan J
  // shipped: two tables, two captions, double scroll.
  return (
    <div className={className}>
      {isEmpty ? (
        empty
      ) : (
      <div className="relative overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          {caption !== undefined && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              {leadingCell !== undefined && (
                <th scope="col" className="w-8 px-2 py-[13px]" aria-hidden="true" />
              )}
              {columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  title={col.headerTitle}
                  className={cx(
                    // Look v3: the report table head, sentence case.
                    "px-4 py-[13px] text-2xs font-semibold text-neutral-500",
                    ALIGN[col.align ?? "left"],
                    col.width,
                  )}
                >
                  {col.header}
                </th>
              ))}
              {hasActions && (
                <th
                  scope="col"
                  className="px-4 py-[13px] text-right text-2xs font-semibold text-neutral-500"
                >
                  {actionsHeader ?? ""}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const visible = (actions ?? []).filter(
                (a) => a.hidden === undefined || !a.hidden(row),
              );
              const extra = rowProps?.(row) ?? {};
              const { className: extraClass, ...extraRest } = extra;
              const detail = expandedRow?.(row) ?? null;
              const key = rowKey(row);
              const body = (
                <tr
                  key={key}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  {...extraRest}
                  className={cx(
                    // An expanded row keeps its border on the DETAIL
                    // row instead, so the pair reads as one block.
                    detail === null && "border-b border-neutral-100 last:border-b-0",
                    "hover:bg-primary-soft/40",
                    onRowClick !== undefined && "cursor-pointer",
                    extraClass,
                  )}
                >
                  {leadingCell !== undefined && (
                    <td className="w-8 px-2 align-middle">{leadingCell(row)}</td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cx(
                        "h-12 px-4 align-middle text-neutral-700",
                        ALIGN[col.align ?? "left"],
                      )}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                  {hasActions && (
                    <td className="h-12 px-4 text-right align-middle">
                      <RowActions row={row} actions={visible} />
                    </td>
                  )}
                </tr>
              );
              if (detail === null) return body;
              return (
                <Fragment key={key}>
                  {body}
                  <tr className="border-b border-neutral-100 last:border-b-0">
                    <td
                      colSpan={
                        columns.length +
                        (leadingCell !== undefined ? 1 : 0) +
                        (hasActions ? 1 : 0)
                      }
                      className="bg-neutral-50 px-4 py-3"
                    >
                      {detail}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      {footnote !== undefined && (
        <p className="mt-3 px-1 text-xs text-neutral-500">{footnote}</p>
      )}
    </div>
  );
}

function resolveIcon<T>(icon: RowAction<T>["icon"], row: T): ReactNode {
  return typeof icon === "function" ? icon(row) : icon;
}

/**
 * Two or fewer actions read better in the open; three or more turn a row
 * into a toolbar, so they go behind a kebab. Destructive actions sort
 * last in both shapes.
 */
export function RowActions<T>({
  row,
  actions,
  menuLabel,
}: {
  row: T;
  actions: RowAction<T>[];
  /** Nombre accesible del kebab. Si no se pasa, se arma con las
   *  etiquetas de las acciones, que el caller ya tradujo. */
  menuLabel?: string;
}): ReactNode {
  if (actions.length === 0) return null;

  const ordered = [...actions].sort(
    (a, b) => Number(a.danger ?? false) - Number(b.danger ?? false),
  );

  if (ordered.length <= 2) {
    return (
      <div className="flex items-center justify-end gap-1">
        {ordered.map((action) => {
          const isDisabled = action.disabled?.(row) ?? false;
          return (
            <button
              key={action.id}
              type="button"
              disabled={isDisabled}
              onClick={(event) => {
                event.stopPropagation();
                action.onSelect(row);
              }}
              className={cx(
                "inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                isDisabled
                  ? "cursor-not-allowed text-neutral-400"
                  : action.danger === true
                    ? "text-error-dark hover:bg-error-soft"
                    : "text-neutral-700 hover:bg-primary-soft",
              )}
            >
              {action.icon !== undefined && (
                <span aria-hidden="true">{resolveIcon(action.icon, row)}</span>
              )}
              {action.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <DropdownMenu
        placement="bottom-end"
        trigger={
          <button
            type="button"
            // Sin esto el botón es un SVG mudo: un lector de pantalla
            // anuncia "botón" doce veces por tabla y no dice de qué.
            // El nombre sale de las acciones que el caller YA tradujo,
            // así que no hace falta i18n en este paquete ni una prop
            // nueva en diez sitios de llamada.
            aria-label={menuLabel ?? ordered.map((a) => a.label).join(", ")}
            aria-haspopup="menu"
            className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-primary-soft hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <MoreHorizontal size={18} />
          </button>
        }
        items={ordered.map((action) => ({
          value: action.id,
          label: action.label,
          icon: resolveIcon(action.icon, row),
          danger: action.danger,
          disabled: action.disabled?.(row) ?? false,
          dividerBefore: action.danger === true,
          onClick: () => action.onSelect(row),
        }))}
      />
    </div>
  );
}
